/**
 * 实体拖拽策略抽象基类。
 * 职责：
 * 1. 定义拖拽策略统一生命周期接口（开始、更新、停止）；
 * 2. 封装拖拽公共上下文（射线投射器、NDC 坐标、渲染器引用）；
 * 3. 子类通过继承实现具体实体类型的拖拽行为。
 */

import * as THREE from 'three';
import type {SketchRuntimeHistorySession, Viewer} from '../../viewer/Viewer';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import {isSketchObject} from '../../mesh/sketch';
import type {SketchParametricDragSession} from '../../sketch/solver/runtime/SketchParametricDragSession';

/**
 * 实体拖拽策略类型枚举。
 */
export enum EntityDragStrategyType {
    /** 点拖拽。 */
    Point = 'point',
    /** 线拖拽。 */
    Line = 'line',
    /** 轮廓拖拽。 */
    Contour = 'contour',
    /** 装配体零件拖拽。 */
    AssemblyPart = 'assembly-part',
}

/**
 * 实体拖拽策略抽象基类。
 */
export abstract class AbstractEntityDragStrategy {
    /** 策略类型标识，用于类型判别。 */
    public abstract readonly strategyType: EntityDragStrategyType;

    /** 渲染器实例。 */
    protected readonly renderer: Viewer;

    /** 射线投射器。 */
    protected readonly raycaster: THREE.Raycaster = new THREE.Raycaster();

    /** 标准化设备坐标。 */
    protected readonly mouseNdc: THREE.Vector2 = new THREE.Vector2();

    /** 当前拖拽流程中实际承载留影的对象。 */
    protected dragPreviewOwner: THREE.Object3D | null = null;
    /** 当前拖拽是否确实修改了草图几何。 */
    private geometryChanged = false;

    /** 当前拖拽对应的草图运行时历史会话。 */
    private sketchRuntimeHistorySession: SketchRuntimeHistorySession | null = null;

    /** 当前拖拽是否仍允许把定义变化提交到草图组。 */
    private definitionChangesCommitAllowed = true;

    /** 拖拽开始前相机控制是否可用。 */
    private cameraControlsEnabledBeforeDrag: boolean | null = null;

    /** 当前拖拽是否由本策略临时禁用了相机控制。 */
    private cameraControlsDisabledByCurrentDrag: boolean = false;

    /**
     * 构造函数。
     * @param renderer 渲染器实例。
     */
    constructor(renderer: Viewer) {
        this.renderer = renderer;
    }

    /**
     * 开始拖拽。
     * 由子类实现具体初始化逻辑。
     */
    public abstract start(payload: RawPointerEventPayload): void;

    /**
     * 更新拖拽位置。
     * @param payload 原始鼠标事件载荷。
     */
    public abstract update(payload: RawPointerEventPayload): void;

    /**
     * 停止拖拽并清理状态。
     */
    public stop(): void {
        try {
            // 结束留影。
            if (this.dragPreviewOwner) {
                this.invokeDragPreviewEnd(this.dragPreviewOwner);
            }
            this.dragPreviewOwner = null;
            this.doStop();
        } finally {
            this.restoreCameraControlsAfterDrag();
        }
    }

    /**
     * 获取当前拖拽是否修改了草图几何。
     * @returns 是否发生几何修改。
     */
    public hasGeometryChanged(): boolean {
        return this.geometryChanged;
    }

    /**
     * 获取当前拖拽是否允许提交草图定义变化。
     *
     * 外部事务替换草图后，旧拖拽会话会被放弃；即使更早的预演曾改变几何，选择处理器也不得
     * 再以旧手势提交 `drag-finished`，否则会把外部事务后的新定义错误归入旧历史步骤。
     *
     * @returns 当前拖拽未被放弃时返回 true。
     */
    public shouldCommitDefinitionChanges(): boolean {
        return this.definitionChangesCommitAllowed;
    }

    /**
     * 解析当前拖拽影响的草图 ID。
     * @returns 草图 ID；未命中时返回 null。
     */
    public resolveAffectedSketchId(): string | null {
        let current: THREE.Object3D | null = this.dragPreviewOwner;
        while (current) {
            if (Reflect.get(current, 'isSketchGroup') === true) {
                return current.uuid;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 子类实现的具体停止逻辑。
     */
    protected abstract doStop(): void;

    /**
     * 标记当前拖拽已修改草图几何。
     */
    protected markGeometryChanged(): void {
        this.geometryChanged = true;
    }

    //------ 拖拽期参数预演（自适应节流） 开始------
    /** 上次拖拽期参数预演的时刻（performance.now()）。 */
    private lastParametricPreviewAt = 0;

    /** 上次拖拽期参数预演的耗时（毫秒），用于自适应节流。 */
    private lastParametricPreviewCostMs = 0;

    /** 因自适应节流尚未执行的最新参数预演；后续请求会覆盖旧请求。 */
    private pendingParametricPreview: (() => boolean) | null = null;

    /**
     * 按上一轮耗时自适应执行参数预演。
     *
     * @param callback 本轮参数预演。
     * @returns callback 结果；本帧被节流时返回 null。
     */
    protected runAdaptiveParametricPreview(callback: () => boolean): boolean | null {
        const now = performance.now();
        const minIntervalMs = Math.max(16, this.lastParametricPreviewCostMs * 2);

        if (now - this.lastParametricPreviewAt < minIntervalMs) {
            // mouseup 不会提供新坐标，只保留最后一次被节流的绝对目标即可。
            this.pendingParametricPreview = callback;
            return null;
        }
        this.pendingParametricPreview = null;
        this.lastParametricPreviewAt = now;

        return this.executeParametricPreview(callback);
    }

    /**
     * 无节流地执行最后一个 pending 参数预演。
     *
     * @returns 存在 pending 请求时返回其执行结果；没有 pending 请求时返回 null。
     */
    protected flushPendingParametricPreview(): boolean | null {
        const callback = this.pendingParametricPreview;
        this.pendingParametricPreview = null;
        if (!callback) {
            return null;
        }

        this.lastParametricPreviewAt = performance.now();
        return this.executeParametricPreview(callback);
    }

    /**
     * 重置一次手势的参数预演节流状态。
     *
     * 开始和结束都重置，避免策略实例被复用时继承上一手势的耗时或 pending 回调。
     */
    protected resetAdaptiveParametricPreviewState(): void {
        this.lastParametricPreviewAt = 0;
        this.lastParametricPreviewCostMs = 0;
        this.pendingParametricPreview = null;
    }

    /**
     * 执行一次参数预演并记录耗时。
     *
     * @param callback 参数预演回调。
     * @returns 参数预演结果。
     */
    private executeParametricPreview(callback: () => boolean): boolean {
        const startedAt = performance.now();
        const result = callback();

        this.lastParametricPreviewCostMs = performance.now() - startedAt;
        return result;
    }

    /**
     * 开启草图拖拽运行时历史会话。
     *
     * 设计说明：
     * 草图拖拽可能同时影响点、边、辅助构造对象、约束容器和上色轮廓。当前 normalized 草图主状态
     * 尚未完整落地，因此拖拽类操作必须先按整组 SketchGroup 快照进入一个模型 undo step。
     *
     * @param label 用户可读历史名称。
     * @param commandType 历史命令类型。
     */
    protected beginSketchRuntimeHistorySession(label: string, commandType: string): void {
        this.cancelSketchRuntimeHistorySession();
        this.definitionChangesCommitAllowed = true;
        this.sketchRuntimeHistorySession = this.renderer.beginActiveSketchRuntimeHistorySession({
            label,
            commandType,
        });
    }

    /**
     * 提交草图拖拽运行时历史会话。
     */
    protected commitSketchRuntimeHistorySession(): void {
        const historySession = this.sketchRuntimeHistorySession;
        this.sketchRuntimeHistorySession = null;

        void historySession?.commit().catch((error) => {
            (globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
        });
    }

    /**
     * 取消草图拖拽运行时历史会话。
     */
    protected cancelSketchRuntimeHistorySession(): void {
        const historySession = this.sketchRuntimeHistorySession;
        this.sketchRuntimeHistorySession = null;
        historySession?.cancel();
    }

    /**
     * 检查参数拖拽会话是否已被外部草图版本替换，并统一放弃旧历史会话。
     *
     * stale 表示 pointerdown 捕获的草图实例或定义版本已不再是当前 authority。此时既不能继续
     * 求解，也不能 cancel 恢复旧 before；只能 discard 释放快照，并阻止选择层提交定义变化。
     *
     * @param parametricDragSession 当前参数拖拽会话。
     * @returns 会话已 stale 且本次拖拽已被放弃时返回 true。
     */
    protected discardStaleSketchDrag(
        parametricDragSession: SketchParametricDragSession | null,
    ): boolean {
        if (!parametricDragSession?.isStale()) {
            return false;
        }

        const historySession = this.sketchRuntimeHistorySession;
        this.sketchRuntimeHistorySession = null;
        this.definitionChangesCommitAllowed = false;
        historySession?.discard();
        return true;
    }

    /**
     * 在拖拽期间临时禁用相机控制。
     *
     * 设计说明：
     * 1. 草图点/轮廓拖拽使用左键，若不禁用相机控制会与视图平移竞争；
     * 2. 仅在拖拽前相机控制处于启用态时才临时关闭，避免覆盖外部已禁用状态；
     * 3. 对应恢复逻辑由 {@link restoreCameraControlsAfterDrag} 统一收口。
     */
    protected disableCameraControlsForDrag(): void {
        const cameraManager = this.renderer.cameraManager;
        const controls = cameraManager?.controls;
        if (!cameraManager || !controls) {
            this.cameraControlsEnabledBeforeDrag = null;
            this.cameraControlsDisabledByCurrentDrag = false;
            return;
        }

        this.cameraControlsEnabledBeforeDrag = controls.enabled;
        if (!controls.enabled) {
            cameraManager.cancelControlsInteraction?.();
            this.cameraControlsDisabledByCurrentDrag = false;
            return;
        }

        cameraManager.cancelControlsInteraction?.();
        cameraManager.setControlsEnabled(false);
        this.cameraControlsDisabledByCurrentDrag = true;
    }

    /**
     * 恢复拖拽前的相机控制状态。
     */
    protected restoreCameraControlsAfterDrag(): void {
        const shouldRestore = this.cameraControlsDisabledByCurrentDrag
            && this.cameraControlsEnabledBeforeDrag === true;
        if (shouldRestore) {
            this.renderer.cameraManager?.cancelControlsInteraction?.();
            this.renderer.cameraManager?.setControlsEnabled(true);
        }

        this.cameraControlsEnabledBeforeDrag = null;
        this.cameraControlsDisabledByCurrentDrag = false;
    }

    /**
     * 将屏幕坐标转换为标准化设备坐标。
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 转换是否成功。
     */
    protected updateMouseNdc(clientX: number, clientY: number): boolean {
        const container = this.renderer.container;
        if (!container) {
            return false;
        }
        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
        return true;
    }

    /**
     * 调用对象的拖拽留影开始回调。
     * @param object 目标对象。
     */
    protected invokeDragPreviewBegin(object: THREE.Object3D): void {
        if (isSketchObject(object)) {
            object.beginDragPreview();
        }
    }

    /**
     * 调用对象的拖拽留影结束回调。
     * @param object 目标对象。
     */
    protected invokeDragPreviewEnd(object: THREE.Object3D): void {
        if (isSketchObject(object)) {
            object.endDragPreview();
        }
    }
}
